import { httpClient } from '../api/http-client';

export interface SalesSummary {
  periodo: {
    fechaInicio: string;
    fechaFin: string;
  };
  totales: {
    ventasOnline: number;
    ventasPOS: number;
    totalGeneral: number;
    cantidadOnline: number;
    cantidadPOS: number;
    totalTransacciones: number;
  };
  promedios: {
    ticketPromedioOnline: number;
    ticketPromedioPOS: number;
    ticketPromedioGeneral: number;
  };
}

export interface SalesByDay {
  fecha: string;
  online: number;
  pos: number;
  total: number;
}

export interface TopProduct {
  id: string;
  nombre: string;
  categoria: string;
  cantidadVendida: number;
  totalVentas: number;
}

export interface SalesByPaymentMethod {
  metodo: string;
  cantidad: number;
  total: number;
  porcentaje: number;
}

export interface OrderDetail {
  id: string;
  numeroPedido: string;
  tipo: 'ONLINE' | 'POS';
  fecha: string;
  cliente: string;
  vendedor: string | null;
  total: number;
  metodoPago: string;
  estado: string;
  items: number;
}

export interface LowStockProduct {
  id: string;
  nombre: string;
  categoria: string;
  stockBase: number;
  stockMinimo: number;
  ubicacion: string | null;
  imagen: string | null;
  presentaciones: Array<{
    nombre: string;
    factor: number;
    stock: number;
  }>;
}

export interface BottomProduct {
  id: string;
  nombre: string;
  categoria: string;
  cantidadVendida: number;
  totalVentas: number;
  stockBase: number;
}

export interface ProductSaleHistoryItem {
  id: string;
  pedidoId: string;
  numeroPedido: string;
  fecha: string;
  vendedor: string | null;
  cantidad: number;
  precioUnitario: number;
  subtotal: number;
  tipo: 'ONLINE' | 'POS';
  cliente: string | null;
}

export interface SoldProduct {
  id: string;
  nombre: string;
  categoria: string;
  codigoBarras: string | null;
  cantidadVendida: number;
  totalVentas: number;
  costoPromedio: number;
  margen: number;
  stockBase: number;
  proveedor: string | null;
  ultimaVenta: string | null;
  vendedores: string | null;
  presentaciones: Array<{
    nombre: string;
    factor: number;
    stock: number;
  }>;
  ventasPorPresentacion: Array<{
    nombre: string;
    cantidad: number;
  }>;
}

export interface SalesByHour {
  hora: number;
  ventas: number;
  transacciones: number;
  online: number;
  pos: number;
}

export interface SalesBySeller {
  vendedorId: string;
  nombre: string;
  email: string;
  totalVentas: number;
  cantidadPedidos: number;
  ticketPromedio: number;
  online: number;
  pos: number;
}

export interface WeekComparison {
  dias: Array<{
    dia: string;
    semanaActual: number;
    semanaAnterior: number;
    variacion: number;
  }>;
  totales: {
    actual: number;
    anterior: number;
    variacion: number;
  };
}

export interface DailyComparison {
  hoy: { total: number; online: number; pos: number; pedidos: number };
  ayer: { total: number; online: number; pos: number; pedidos: number };
  variacion: { total: number; online: number; pos: number; pedidos: number };
}

export interface ExpiringProduct {
  loteId: string;
  numeroLote: string;
  cantidad: number;
  fechaVencimiento: string;
  productoId: string;
  productoNombre: string;
  categoria: string;
  presentacionNombre: string | null;
  precioUnitario: number;
  valorPerdidaEstimado: number;
  diasParaVencer: number;
}

export interface BajaProductoItem {
  id: string;
  productoId: string;
  producto: {
    id: string;
    nombre: string;
    categoria: string;
    codigoBarras: string | null;
  };
  motivo: string;
  cantidad: number;
  observaciones: string | null;
  usuario: {
    id: string;
    nombreCompleto: string;
  } | null;
  fechaBaja: string;
}

export interface ReportFilters {
  fechaInicio?: string;
  fechaFin?: string;
  tipo?: 'ONLINE' | 'POS' | 'ALL';
  vendedorId?: string;
  proveedorId?: string;
  search?: string;
}

export class ReportsService {
  static async getDashboard(vendedorId?: string, proveedorId?: string, fechaInicio?: string, fechaFin?: string) {
    const params = new URLSearchParams();
    if (vendedorId) params.append('vendedorId', vendedorId);
    if (proveedorId) params.append('proveedorId', proveedorId);
    if (fechaInicio) params.append('fechaInicio', fechaInicio);
    if (fechaFin) params.append('fechaFin', fechaFin);
    const response = await httpClient.get<{ success: boolean; data: any }>(`/reports/dashboard?${params.toString()}`);
    return response.data;
  }

  static async getSalesSummary(filters: ReportFilters) {
    const params = new URLSearchParams();
    if (filters.fechaInicio) params.append('fechaInicio', filters.fechaInicio);
    if (filters.fechaFin) params.append('fechaFin', filters.fechaFin);
    if (filters.tipo) params.append('tipo', filters.tipo);
    if (filters.vendedorId) params.append('vendedorId', filters.vendedorId);
    if (filters.proveedorId) params.append('proveedorId', filters.proveedorId);
    
    const response = await httpClient.get<{ success: boolean; data: SalesSummary }>(
      `/reports/sales-summary?${params.toString()}`
    );
    return response.data;
  }

  static async getSalesByDay(filters: ReportFilters) {
    const params = new URLSearchParams();
    if (filters.fechaInicio) params.append('fechaInicio', filters.fechaInicio);
    if (filters.fechaFin) params.append('fechaFin', filters.fechaFin);
    if (filters.vendedorId) params.append('vendedorId', filters.vendedorId);
    if (filters.proveedorId) params.append('proveedorId', filters.proveedorId);
    
    const response = await httpClient.get<{ success: boolean; data: SalesByDay[] }>(
      `/reports/sales-by-day?${params.toString()}`
    );
    return response.data;
  }

  static async getTopProducts(filters: ReportFilters, limit: number = 10) {
    const params = new URLSearchParams();
    if (filters.fechaInicio) params.append('fechaInicio', filters.fechaInicio);
    if (filters.fechaFin) params.append('fechaFin', filters.fechaFin);
    if (filters.vendedorId) params.append('vendedorId', filters.vendedorId);
    if (filters.proveedorId) params.append('proveedorId', filters.proveedorId);
    params.append('limit', limit.toString());
    
    const response = await httpClient.get<{ success: boolean; data: TopProduct[] }>(
      `/reports/top-products?${params.toString()}`
    );
    return response.data;
  }

  static async getSalesByPaymentMethod(filters: ReportFilters) {
    const params = new URLSearchParams();
    if (filters.fechaInicio) params.append('fechaInicio', filters.fechaInicio);
    if (filters.fechaFin) params.append('fechaFin', filters.fechaFin);
    if (filters.vendedorId) params.append('vendedorId', filters.vendedorId);
    if (filters.proveedorId) params.append('proveedorId', filters.proveedorId);
    
    const response = await httpClient.get<{ success: boolean; data: SalesByPaymentMethod[] }>(
      `/reports/payment-methods?${params.toString()}`
    );
    return response.data;
  }

  static async getOrdersDetail(filters: ReportFilters, page: number = 1, limit: number = 20) {
    const params = new URLSearchParams();
    if (filters.fechaInicio) params.append('fechaInicio', filters.fechaInicio);
    if (filters.fechaFin) params.append('fechaFin', filters.fechaFin);
    if (filters.tipo) params.append('tipo', filters.tipo);
    if (filters.vendedorId) params.append('vendedorId', filters.vendedorId);
    if (filters.proveedorId) params.append('proveedorId', filters.proveedorId);
    if (filters.search) params.append('search', filters.search);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    const response = await httpClient.get<{ success: boolean; data: { data: OrderDetail[]; meta: any } }>(
      `/reports/orders?${params.toString()}`
    );
    return response.data;
  }

  static async getSoldProducts(filters: ReportFilters, page: number = 1, limit: number = 20) {
    const params = new URLSearchParams();
    if (filters.fechaInicio) params.append('fechaInicio', filters.fechaInicio);
    if (filters.fechaFin) params.append('fechaFin', filters.fechaFin);
    if (filters.vendedorId) params.append('vendedorId', filters.vendedorId);
    if (filters.proveedorId) params.append('proveedorId', filters.proveedorId);
    if (filters.search) params.append('search', filters.search);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    const response = await httpClient.get<{ success: boolean; data: { data: SoldProduct[]; meta: any } }>(
      `/reports/sold-products?${params.toString()}`
    );
    return response.data;
  }

  static async getProductSaleHistory(productId: string, filters: ReportFilters, page = 1, limit = 20) {
    const params = new URLSearchParams();
    if (filters.fechaInicio) params.append('fechaInicio', filters.fechaInicio);
    if (filters.fechaFin) params.append('fechaFin', filters.fechaFin);
    if (filters.vendedorId) params.append('vendedorId', filters.vendedorId);
    if (filters.proveedorId) params.append('proveedorId', filters.proveedorId);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    const response = await httpClient.get<{ success: boolean; data: { data: ProductSaleHistoryItem[]; meta: any } }>(
      `/reports/sold-products/${encodeURIComponent(productId)}/history?${params.toString()}`
    );
    return response.data;
  }

  static async getSalesByHour(filters: ReportFilters) {
    const params = new URLSearchParams();
    if (filters.fechaInicio) params.append('fechaInicio', filters.fechaInicio);
    if (filters.fechaFin) params.append('fechaFin', filters.fechaFin);
    if (filters.vendedorId) params.append('vendedorId', filters.vendedorId);
    if (filters.proveedorId) params.append('proveedorId', filters.proveedorId);
    
    const response = await httpClient.get<{ success: boolean; data: SalesByHour[] }>(
      `/reports/sales-by-hour?${params.toString()}`
    );
    return response.data;
  }

  static async getSalesBySeller(filters: ReportFilters) {
    const params = new URLSearchParams();
    if (filters.fechaInicio) params.append('fechaInicio', filters.fechaInicio);
    if (filters.fechaFin) params.append('fechaFin', filters.fechaFin);
    if (filters.vendedorId) params.append('vendedorId', filters.vendedorId);
    if (filters.proveedorId) params.append('proveedorId', filters.proveedorId);
    
    const response = await httpClient.get<{ success: boolean; data: SalesBySeller[] }>(
      `/reports/sales-by-seller?${params.toString()}`
    );
    return response.data;
  }

  static async getWeekComparison(vendedorId?: string, proveedorId?: string) {
    const params = new URLSearchParams();
    if (vendedorId) params.append('vendedorId', vendedorId);
    if (proveedorId) params.append('proveedorId', proveedorId);
    const response = await httpClient.get<{ success: boolean; data: WeekComparison }>(
      `/reports/week-comparison?${params.toString()}`
    );
    return response.data;
  }

  static async getDailyComparison(vendedorId?: string, proveedorId?: string) {
    const params = new URLSearchParams();
    if (vendedorId) params.append('vendedorId', vendedorId);
    if (proveedorId) params.append('proveedorId', proveedorId);
    const response = await httpClient.get<{ success: boolean; data: DailyComparison }>(
      `/reports/daily-comparison?${params.toString()}`
    );
    return response.data;
  }

  static async getExpiringProducts(days?: number, proveedorId?: string) {
    const params = new URLSearchParams();
    if (days) params.append('days', days.toString());
    if (proveedorId) params.append('proveedorId', proveedorId);
    const response = await httpClient.get<{ success: boolean; data: ExpiringProduct[] }>(
      `/reports/expiring-products?${params.toString()}`
    );
    return response.data;
  }

  static async getBottomProducts(filters: ReportFilters, limit: number = 50) {
    const params = new URLSearchParams();
    if (filters.fechaInicio) params.append('fechaInicio', filters.fechaInicio);
    if (filters.fechaFin) params.append('fechaFin', filters.fechaFin);
    if (filters.proveedorId) params.append('proveedorId', filters.proveedorId);
    params.append('limit', limit.toString());
    const response = await httpClient.get<{ success: boolean; data: BottomProduct[] }>(
      `/reports/bottom-products?${params.toString()}`
    );
    return response.data;
  }

  static async getAvailableMonths() {
    const response = await httpClient.get<{ success: boolean; data: string[] }>(`/reports/available-months`);
    return response.data;
  }
}
